import React, { useState } from 'react';
import axios from 'axios';
import { formatDateOnly } from '../utils/dateFormatter';
import styles from './StepTimeline.module.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://wsticket.frozentoasty.de/api';

const StepTimeline = ({ steps, stepTimestamps, ticketId, selectedDate, onStepAdded, onStepAdding }) => {
  const [newStepText, setNewStepText] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  // Fix 30.10.2026: Steps und stepTimestamps sind index-gekoppelt - Paarung per
  // Index statt Textmatch (identische Step-Texte verbanden sonst falsche Timestamps)
  const stepsForDate = (steps || [])
    .map((text, index) => ({
      text,
      createdAt: stepTimestamps?.[index]?.createdAt || null
    }))
    .filter(item => {
      if (!item.createdAt) return false;
      const d = new Date(item.createdAt);
      const localDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      return localDate === selectedDate;
    })
    // Neueste zuoberst (Fix 30.10.2026: Array-Reihenfolge ignorierte Zeitsortierung)
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const handleAddStep = async () => {
    if (!newStepText.trim()) return;

    setIsAdding(true);
    // Parent vor dem POST informieren (setzt Socket-Echo-Guard, damit das
    // eigene ticketUpdated-Event nicht doch noch einen Refetch auslöst)
    if (onStepAdding) {
      onStepAdding();
    }
    try {
      // Zeitstempel auf den angesehenen Tag buchen: heute = jetzt, vergangene
      // Tage = selectedDate mit aktueller Uhrzeit (Fix 30.10.2026)
      const now = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      const todayStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
      let createdAt = now.toISOString();
      if (selectedDate && selectedDate !== todayStr) {
        const dt = new Date(`${selectedDate}T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`);
        if (!isNaN(dt.getTime()) && dt.getTime() < now.getTime()) {
          createdAt = dt.toISOString();
        }
      }

      // API Call zum Step-Hinzufügen
      const response = await axios.post(`${API_URL}/tickets/${ticketId}/steps`, {
        step: newStepText.trim(),
        createdAt
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setNewStepText('');
      // Parent-Component targeted aktualisieren (ohne Modal-Reload, Fix 30.10.2026)
      if (onStepAdded) {
        onStepAdded(response.data);
      }
    } catch (error) {
      console.error('Error adding step:', error);
      alert('Fehler beim Hinzufügen des Steps');
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <div className={styles.timelineContainer}>
      <h5 className={styles.timelineHeader}>Step-Timeline</h5>
      {stepsForDate.length === 0 ? (
        <div className={styles.noSteps}>
          Keine Steps für dieses Datum vorhanden
        </div>
      ) : (
        <div className={styles.timeline}>
          {stepsForDate.map((step, idx) => (
            <div key={idx} className={styles.timelineItem}>
              {/* Timeline-Punkt */}
              <div className={styles.timelineDot}>
                <div className={`${styles.dot} ${step.createdAt ? styles.dotActive : styles.dotInactive}`} />
                {idx < stepsForDate.length - 1 && (
                  <div className={styles.timelineLine} />
                )}
              </div>

              {/* Step Content */}
              <div className={styles.stepContent}>
                <div className={styles.stepText}>{step.text}</div>
                <div className={`${styles.stepDate} ${!step.createdAt ? styles.stepDateMissing : ''}`}>
                  {step.createdAt ? formatDateOnly(step.createdAt) : 'Kein Datum'}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Step-Hinzufügen Form - immer anzeigen */}
      <div className={styles.addStepContainer}>
        <input
          type="text"
          value={newStepText}
          onChange={(e) => setNewStepText(e.target.value)}
          placeholder="Neuen Step hinzufügen..."
          className={styles.addStepInput}
          disabled={isAdding}
          onKeyPress={(e) => e.key === 'Enter' && handleAddStep()}
        />
        <button
          onClick={handleAddStep}
          className={styles.addStepButton}
          disabled={isAdding || !newStepText.trim()}
        >
          {isAdding ? 'Wird hinzugefügt...' : 'Hinzufügen'}
        </button>
      </div>
    </div>
  );
};

export default StepTimeline;
